import { useState } from 'react'
import { format, parseISO } from 'date-fns'
import { ChevronDown, ChevronUp, CheckCircle2, MinusCircle, XCircle, Trash2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import { useDeleteSummary } from '@/hooks/useAiSummaries'
import { modelLabel } from '@/lib/modelLabel'
import { LetterBody } from './LetterBody'
import { ThinkingPanel } from './ThinkingPanel'
import type { AiSummary } from '@/types'

// ─── Mode labels ──────────────────────────────────────────────────────────────

const MODE_LABELS: Record<string, string> = {
  integrated:           'Integrated',
  biblical_discernment: 'Discernment',
  systems_thinker:      'Systems',
  trusted_friend:       'Friend',
}

// ─── Alignment sparkline ──────────────────────────────────────────────────────

function AlignmentSparkline({ scores }: { scores: number[] }) {
  if (scores.length < 2) return null

  const W = 180, H = 40, pad = 6
  const min = Math.min(...scores)
  const max = Math.max(...scores)
  const range = max - min || 10  // avoid flat line when all scores equal
  const pts = scores
    .map((v, i) => {
      const x = pad + (i / (scores.length - 1)) * (W - 2 * pad)
      const y = H - pad - ((v - min) / range) * (H - 2 * pad)
      return `${x.toFixed(1)},${y.toFixed(1)}`
    })
    .join(' ')

  const lastScore = scores[scores.length - 1]
  const lastX = W - pad
  const lastY = H - pad - ((lastScore - min) / range) * (H - 2 * pad)

  return (
    <div className="space-y-1">
      <p className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">
        Alignment trend
      </p>
      <div className="flex items-end gap-3">
        <svg
          width={W}
          height={H}
          viewBox={`0 0 ${W} ${H}`}
          className="text-primary overflow-visible"
        >
          <polyline
            points={pts}
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinejoin="round"
            strokeLinecap="round"
            opacity={0.7}
          />
          <circle cx={lastX} cy={lastY} r="3" fill="currentColor" />
        </svg>
        <span className="text-xs text-muted-foreground tabular-nums pb-1">
          {scores[0]} → <span className="text-foreground font-medium">{lastScore}</span>
        </span>
      </div>
    </div>
  )
}

// ─── Score chip ───────────────────────────────────────────────────────────────

function ScoreChip({ score }: { score: number | null }) {
  if (score === null) return <span className="text-xs text-muted-foreground/50">—</span>
  return (
    <span
      className={cn(
        'text-[11px] px-1.5 py-0.5 rounded-full border tabular-nums',
        score >= 70
          ? 'border-emerald-300 text-emerald-700 bg-emerald-50 dark:bg-emerald-950/30 dark:border-emerald-700 dark:text-emerald-300'
          : score >= 45
            ? 'border-amber-300 text-amber-700 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-700 dark:text-amber-300'
            : 'border-red-300 text-red-700 bg-red-50 dark:bg-red-950/30 dark:border-red-700 dark:text-red-300',
      )}
    >
      {score}
    </span>
  )
}

// ─── Feedback icon ────────────────────────────────────────────────────────────

function FeedbackIcon({ rating }: { rating?: string }) {
  if (rating === 'landed') return <CheckCircle2 size={13} className="text-emerald-500 flex-shrink-0" />
  if (rating === 'mixed')  return <MinusCircle  size={13} className="text-amber-500 flex-shrink-0" />
  if (rating === 'missed') return <XCircle      size={13} className="text-red-500 flex-shrink-0" />
  return null
}

// ─── Single history row ───────────────────────────────────────────────────────

function HistoryRow({ summary }: { summary: AiSummary }) {
  const [expanded, setExpanded] = useState(false)
  const [thinkingOpen, setThinkingOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false)
  const deleteSummary = useDeleteSummary()

  async function handleDelete() {
    setDeleting(true)
    try {
      await deleteSummary.mutateAsync(summary.id)
    } catch {
      setDeleting(false)
    }
  }

  return (
    <div className="border border-border rounded-md overflow-hidden">
      {/* Row header */}
      <div
        role="button"
        tabIndex={0}
        onClick={() => setExpanded(v => !v)}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') setExpanded(v => !v) }}
        className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-muted/40 transition-colors cursor-pointer"
      >
        <div className="flex-1 min-w-0 flex items-center gap-2.5 flex-wrap">
          <span className="text-xs font-medium text-foreground tabular-nums">
            {format(parseISO(summary.createdAt), 'MMM d, yyyy')}
          </span>
          <span className="text-[11px] text-muted-foreground">
            {MODE_LABELS[summary.coachingMode] ?? summary.coachingMode}
            {summary.model && <> · {modelLabel(summary.model)}</>}
          </span>
          <ScoreChip score={summary.body.alignmentScore} />
          <FeedbackIcon rating={summary.feedbackRating} />
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setConfirmDeleteOpen(true) }}
            onKeyDown={(e) => e.stopPropagation()}
            disabled={deleting}
            className="p-1 rounded text-muted-foreground/40 hover:text-destructive hover:bg-destructive/10 transition-colors"
            aria-label="Delete letter"
          >
            <Trash2 size={12} />
          </button>
          {expanded
            ? <ChevronUp size={14} className="text-muted-foreground" />
            : <ChevronDown size={14} className="text-muted-foreground" />
          }
        </div>
      </div>

      {/* Expanded letter */}
      {expanded && (
        <div className="px-3 pb-4 pt-1 border-t border-border space-y-4">
          {summary.feedbackNote && (
            <p className="text-xs text-muted-foreground italic border-l-2 border-border pl-3">
              Your note: {summary.feedbackNote}
            </p>
          )}
          {summary.thinking && (
            <ThinkingPanel text={summary.thinking} open={thinkingOpen} onOpenChange={setThinkingOpen} />
          )}
          <LetterBody
            body={summary.body}
            distressFloorTriggered={summary.distressFloorTriggered}
          />
        </div>
      )}

      <ConfirmDialog
        open={confirmDeleteOpen}
        onOpenChange={setConfirmDeleteOpen}
        title="Delete this letter?"
        description="This coaching letter will be permanently deleted and cannot be recovered."
        confirmLabel="Delete letter"
        destructive
        onConfirm={handleDelete}
      />
    </div>
  )
}

// ─── Main component ───────────────────────────────────────────────────────────

export function CoachingHistory({ summaries }: { summaries: AiSummary[] }) {
  if (summaries.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-2 text-muted-foreground">
        <p className="text-sm">No letters yet.</p>
        <p className="text-xs">Generate your first coaching letter to start building history.</p>
      </div>
    )
  }

  // Scores in chronological order (oldest first) for the sparkline
  const scores = [...summaries]
    .reverse()
    .map(s => s.body.alignmentScore)
    .filter((s): s is number => s !== null && s !== undefined)

  return (
    <div className="space-y-5">
      {scores.length >= 2 && <AlignmentSparkline scores={scores} />}

      <div className="space-y-2">
        {summaries.map(s => (
          <HistoryRow key={s.id} summary={s} />
        ))}
      </div>
    </div>
  )
}
